import React from "react";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { ThemeProvider as EmotionThemeProvider } from "@emotion/react";
import { darkTheme } from "src/Theme/darkTheme";
import { QueryClient, QueryClientProvider } from "react-query";
import { ReactQueryDevtools } from "react-query/devtools";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { Auth0Provider } from "@auth0/auth0-react";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { IoProvider } from "socket.io-react-hook";
import { AppModeProvider } from "src/Common/context/AppModeContext";

const theme = createTheme(darkTheme);
const queryClient = new QueryClient();

// Check if auth is disabled for local development
// Set to true to bypass Auth0 login (for local dev)
export const AUTH_DISABLED = 
  import.meta.env.VITE_AUTH_DISABLED === "true" || 
  import.meta.env.DEV; // Always disable in Vite dev mode

const AUTH0_DOMAIN = import.meta.env.VITE_AUTH0_DOMAIN || "placeholder.auth0.com";
const AUTH0_CLIENT_ID = import.meta.env.VITE_AUTH0_CLIENT_ID || "placeholder-client-id";
const AUTH0_AUDIENCE = import.meta.env.VITE_AUTH0_AUDIENCE || "";

// Inner providers without Auth0
const CoreProviders = ({ children }: { children: React.ReactNode }) => (
  <IoProvider>
    <ThemeProvider theme={theme}>
      <EmotionThemeProvider theme={theme}>
        <QueryClientProvider client={queryClient}>
          <LocalizationProvider dateAdapter={AdapterLuxon}>
            <AppModeProvider>
              <ToastContainer />
              {children}
              <ReactQueryDevtools initialIsOpen={false} />
            </AppModeProvider>
          </LocalizationProvider>
        </QueryClientProvider>
      </EmotionThemeProvider>
    </ThemeProvider>
  </IoProvider>
);

export const Providers = ({ children }: { children: React.ReactNode }) => {
  // Skip Auth0 completely when disabled (for local development)
  if (AUTH_DISABLED) {
    return <CoreProviders>{children}</CoreProviders>;
  }

  return (
    <Auth0Provider
      domain={AUTH0_DOMAIN}
      clientId={AUTH0_CLIENT_ID}
      redirectUri={window.location.origin}
      audience={AUTH0_AUDIENCE}
      cacheLocation="localstorage"
      useRefreshTokens
    >
      <CoreProviders>{children}</CoreProviders>
    </Auth0Provider>
  );
};
